// =============================================================================
// API Utilities - 通用 API 调用工具
// =============================================================================

import { API_CONFIG } from '../config/api.js';

/**
 * 构建完整的 API URL
 * @param {string} path - API 路径（如 '/api/workspaces'）
 * @returns {string} 完整的 URL
 */
export function buildApiUrl(path) {
  if (path.startsWith('http')) {
    return path; // 已经是完整 URL
  }
  
  // 确保路径以 / 开头
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  return `${API_CONFIG.BASE_URL}${normalizedPath}`;
}

/**
 * 获取认证头
 * @returns {Object} 包含认证信息的头部对象
 */
export function getAuthHeaders() {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  return headers;
}

/**
 * 通用 API 调用函数
 * @param {string} url - API 路径或完整 URL
 * @param {Object} options - fetch 选项
 * @returns {Promise<Response>} fetch 响应
 */
export async function apiCall(url, options = {}) {
  const headers = getAuthHeaders();
  const fullUrl = buildApiUrl(url);
  
  const response = await fetch(fullUrl, {
    ...options,
    headers: {
      ...headers,
      ...options.headers
    }
  });
  
  // 处理认证失败
  if (response.status === 401) {
    console.warn('Authentication failed, redirecting to login');
    localStorage.removeItem('auth_token');
    window.location.href = '/#/login';
    throw new Error('Authentication failed');
  }
  
  return response;
}

/**
 * API 调用并解析 JSON
 * @param {string} url - API 路径或完整 URL
 * @param {Object} options - fetch 选项
 * @returns {Promise<Object>} 解析后的 JSON 数据
 */
export async function apiCallJson(url, options = {}) {
  const response = await apiCall(url, options);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
  }
  
  return await response.json();
}

/**
 * GET 请求
 * @param {string} url - API 路径
 * @param {Object} options - 额外选项
 * @returns {Promise<Object>} JSON 响应
 */
export async function apiGet(url, options = {}) {
  return apiCallJson(url, { ...options, method: 'GET' });
}

/**
 * POST 请求
 * @param {string} url - API 路径
 * @param {Object} data - 请求数据
 * @param {Object} options - 额外选项
 * @returns {Promise<Object>} JSON 响应
 */
export async function apiPost(url, data = null, options = {}) {
  return apiCallJson(url, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined
  });
}

/**
 * PUT 请求
 * @param {string} url - API 路径
 * @param {Object} data - 请求数据
 * @param {Object} options - 额外选项
 * @returns {Promise<Object>} JSON 响应
 */
export async function apiPut(url, data = null, options = {}) {
  return apiCallJson(url, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined
  });
}

/**
 * DELETE 请求
 * @param {string} url - API 路径
 * @param {Object} options - 额外选项
 * @returns {Promise<Object>} JSON 响应
 */
export async function apiDelete(url, options = {}) {
  return apiCallJson(url, { ...options, method: 'DELETE' });
}

/**
 * 检查 API 响应是否成功
 * @param {Object} response - API 响应对象
 * @returns {boolean} 是否成功
 */
export function isApiSuccess(response) {
  return response && response.success === true;
}

/**
 * 从 API 响应中提取数据
 * @param {Object} response - API 响应对象
 * @returns {*} 响应数据
 */
export function extractApiData(response) {
  if (isApiSuccess(response)) {
    return response.data;
  }
  throw new Error(response?.error || response?.message || 'API call failed');
}
